import React, { useEffect, useState } from "react";
import { Mail, MailX, MailCheck, Loader2, AlertCircle, CheckCircle2, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import api, { readError } from "@/lib/api";

const STATUSES = ["all", "sent", "failed", "skipped_no_smtp"];

function StatusBadge({ s }) {
  if (s === "sent") return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="badge-sent"><MailCheck className="h-3 w-3 mr-1" strokeWidth={2} />Sent</Badge>;
  if (s === "failed") return <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100" data-testid="badge-failed"><MailX className="h-3 w-3 mr-1" strokeWidth={2} />Failed</Badge>;
  if (s === "skipped_no_smtp") return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="badge-skipped"><AlertCircle className="h-3 w-3 mr-1" strokeWidth={2} />Skipped (no SMTP)</Badge>;
  return <Badge variant="secondary" data-testid={`badge-${s || "unknown"}`}>{s || "unknown"}</Badge>;
}

export default function AdminEmailLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/admin/email-log", { params: { limit: 100, ...params } });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setError(readError(e, "Failed to load email log"));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-email-log">
      <header>
        <h1 className="font-serif text-3xl text-foreground">Email Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Last 100 outgoing emails — newest first. <span className="text-foreground/70">{total} total</span>
        </p>
      </header>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="email-log-filter-tabs">
          {STATUSES.map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`tab-${s}`}>{s === "all" ? "All" : s === "skipped_no_smtp" ? "Skipped" : s.charAt(0).toUpperCase() + s.slice(1)}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex items-center gap-3 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading…</div>
          ) : error ? (
            <div className="p-6 text-sm text-destructive" data-testid="email-log-error">{error}</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="email-log-empty">
              <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" strokeWidth={1.2} />
              No emails logged{filter !== "all" ? ` for status "${filter}"` : ""} yet.
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="email-log-list">
              {items.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(m)}
                    data-testid={`email-log-row-${m.id}`}
                    className="w-full px-5 py-3.5 flex items-start gap-4 hover:bg-muted/40 transition-colors text-left"
                  >
                    <div className="flex-shrink-0 mt-0.5"><StatusBadge s={m.status} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-3 justify-between">
                        <span className="text-sm text-foreground font-medium truncate">{m.subject || "(no subject)"}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{(m.created_at || "").slice(0, 19).replace("T", " ")}</span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground flex items-center gap-2 truncate">
                        <span className="truncate">{m.to}</span>
                        <span className="text-muted-foreground/60">·</span>
                        <span className="font-mono uppercase tracking-wider">{m.template}</span>
                        <span className="text-muted-foreground/60">·</span>
                        <span>{m.locale}</span>
                      </div>
                      {m.error && <div className="mt-1 text-xs text-rose-700 truncate">{m.error}</div>}
                    </div>
                    <ChevronRight className="h-4 w-4 mt-1 text-muted-foreground/60" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-card text-foreground w-full sm:max-w-lg overflow-y-auto" data-testid="email-log-detail">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl pr-4">{selected.subject}</SheetTitle>
                <SheetDescription className="text-xs">Logged {(selected.created_at || "").replace("T", " ")}</SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-4 text-sm">
                <Row label="To" value={selected.to} />
                <Row label="Template" value={selected.template} mono />
                <Row label="Locale" value={selected.locale} />
                <Row label="Status" value={<StatusBadge s={selected.status} />} />
                {selected.error && <Row label="Error" value={<span className="text-rose-700 whitespace-pre-wrap font-mono text-xs">{selected.error}</span>} />}
                {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                  <Row label="Metadata" value={
                    <pre className="bg-muted/50 rounded-lg p-3 text-xs whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(selected.metadata, null, 2)}
                    </pre>
                  } />
                )}
                {selected.status === "sent" && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-sm text-emerald-900 inline-flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5" strokeWidth={1.5} />
                    Email delivered to SMTP server. Inbox delivery depends on the recipient's email provider.
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 items-baseline">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono text-foreground/90" : "text-foreground"}>{value}</div>
    </div>
  );
}
